import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectContext } from '../contexts/ProjectContext';
import { useSaveImmobile } from '../hooks/useImmobile';
import { calcolaCommissioneAgenzia } from '../services/taxCalculations';
import type { CreateImmobileInput } from '../types';

const TIPI_IMMOBILE = [
  'Appartamento',
  'Villa',
  'Villetta a schiera',
  'Attico',
  'Loft',
  'Monolocale',
  'Bilocale',
  'Rustico',
  'Altro',
];

const CLASSI_ENERGETICHE = ['A4', 'A3', 'A2', 'A1', 'B', 'C', 'D', 'E', 'F', 'G'];

// Fields that unlock specific sections
const UNLOCK_MAP: Record<string, string[]> = {
  prezzoAcquisto: ['Notaio'],
  primaCasa: ['Notaio'],
  dataRogito: ['Gantt', 'Flussi Cassa'],
  renditaCatastale: ['Detrazioni'],
  classeEnergetica: ['Detrazioni'],
};

function formatCurrency(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

export function SchedaImmobile() {
  const { currentProject } = useProjectContext();
  const saveImmobile = useSaveImmobile();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    prezzoAcquisto: '',
    indirizzo: '',
    tipoImmobile: '',
    superficie: '',
    annoCostruzione: '',
    dataRogito: '',
    primaCasa: true,
    venditoreImpresa: false,
    percentualeAgenzia: '3',
    renditaCatastale: '',
    classeEnergetica: '',
    note: '',
  });

  // Populate form when project data loads
  useEffect(() => {
    const imm = currentProject?.immobile;
    if (imm) {
      setForm({
        prezzoAcquisto: imm.prezzoAcquisto || '',
        indirizzo: imm.indirizzo || '',
        tipoImmobile: imm.tipoImmobile || '',
        superficie: imm.superficie || '',
        annoCostruzione: imm.annoCostruzione?.toString() || '',
        dataRogito: imm.dataRogito ? imm.dataRogito.slice(0, 10) : '',
        primaCasa: imm.primaCasa,
        venditoreImpresa: imm.venditoreImpresa,
        percentualeAgenzia: imm.percentualeAgenzia || '3',
        renditaCatastale: imm.renditaCatastale || '',
        classeEnergetica: imm.classeEnergetica || '',
        note: imm.note || '',
      });
    }
  }, [currentProject?.immobile]);

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Seleziona un progetto per compilare la scheda immobile</p>
      </div>
    );
  }

  const handleChange = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const commissionePreview = useMemo(() => {
    const prezzo = parseFloat(form.prezzoAcquisto);
    const perc = parseFloat(form.percentualeAgenzia);
    if (!prezzo || !perc) return null;
    const totale = calcolaCommissioneAgenzia(prezzo, perc);
    const netta = prezzo * (perc / 100);
    return { totale, netta, iva: totale - netta };
  }, [form.prezzoAcquisto, form.percentualeAgenzia]);

  const handleSave = () => {
    const payload: CreateImmobileInput = {
      projectId: currentProject.id,
      primaCasa: form.primaCasa,
      venditoreImpresa: form.venditoreImpresa,
    };

    if (form.prezzoAcquisto) payload.prezzoAcquisto = form.prezzoAcquisto;
    if (form.indirizzo) payload.indirizzo = form.indirizzo;
    if (form.tipoImmobile) payload.tipoImmobile = form.tipoImmobile;
    if (form.superficie) payload.superficie = form.superficie;
    if (form.annoCostruzione) payload.annoCostruzione = parseInt(form.annoCostruzione, 10);
    if (form.dataRogito) payload.dataRogito = new Date(form.dataRogito).toISOString();
    if (form.percentualeAgenzia) payload.percentualeAgenzia = form.percentualeAgenzia;
    if (form.renditaCatastale) payload.renditaCatastale = form.renditaCatastale;
    if (form.classeEnergetica) payload.classeEnergetica = form.classeEnergetica;
    if (form.note) payload.note = form.note;

    saveImmobile.mutate(payload, {
      onSuccess: () => {
        navigate('/dashboard');
      },
    });
  };

  const renderUnlockBadges = (field: string) => {
    const sections = UNLOCK_MAP[field];
    if (!sections) return null;
    return (
      <span className="ml-2 inline-flex gap-1">
        {sections.map((s) => (
          <span
            key={s}
            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
            title={`Compila questo campo per sbloccare ${s}`}
          >
            {s}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Scheda Immobile</h2>
          <p className="text-gray-600 mt-1">Dati dell'immobile per il progetto {currentProject.name}</p>
        </div>
        {saveImmobile.isPending && (
          <span className="text-sm text-gray-500 flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            Salvataggio...
          </span>
        )}
      </div>

      {saveImmobile.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Errore nel salvataggio: {saveImmobile.error?.message}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sezione 1 — Dati Base */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Dati Base</h3>
          <div className="space-y-4">
            {/* Prezzo acquisto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prezzo Acquisto
                {renderUnlockBadges('prezzoAcquisto')}
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">€</span>
                <input
                  type="number"
                  value={form.prezzoAcquisto}
                  onChange={(e) => handleChange('prezzoAcquisto', e.target.value)}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="250000"
                  min="0"
                  step="1000"
                />
              </div>
              {form.prezzoAcquisto && (
                <p className="mt-1 text-xs text-gray-500">{formatCurrency(form.prezzoAcquisto)}</p>
              )}
            </div>

            {/* Indirizzo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
              <input
                type="text"
                value={form.indirizzo}
                onChange={(e) => handleChange('indirizzo', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Via Roma 1, Milano"
              />
            </div>

            {/* Tipo immobile */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Immobile</label>
              <select
                value={form.tipoImmobile}
                onChange={(e) => handleChange('tipoImmobile', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Seleziona...</option>
                {TIPI_IMMOBILE.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Superficie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Superficie</label>
              <div className="relative">
                <input
                  type="number"
                  value={form.superficie}
                  onChange={(e) => handleChange('superficie', e.target.value)}
                  className="w-full pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="85"
                  min="0"
                  step="1"
                />
                <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">mq</span>
              </div>
            </div>

            {/* Anno costruzione */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anno Costruzione</label>
              <input
                type="number"
                value={form.annoCostruzione}
                onChange={(e) => handleChange('annoCostruzione', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="2005"
                min="1800"
                max="2100"
              />
            </div>
          </div>
        </div>

        {/* Sezione 2 — Acquisto */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Dati Acquisto</h3>
          <div className="space-y-4">
            {/* Data rogito */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Rogito
                {renderUnlockBadges('dataRogito')}
              </label>
              <input
                type="date"
                value={form.dataRogito}
                onChange={(e) => handleChange('dataRogito', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Prima casa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prima Casa
                {renderUnlockBadges('primaCasa')}
              </label>
              <div className="flex gap-4 mt-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="primaCasa"
                    checked={form.primaCasa}
                    onChange={() => handleChange('primaCasa', true)}
                    className="form-radio text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Si</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="primaCasa"
                    checked={!form.primaCasa}
                    onChange={() => handleChange('primaCasa', false)}
                    className="form-radio text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">No</span>
                </label>
              </div>
            </div>

            {/* Venditore */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Venditore</label>
              <div className="flex gap-4 mt-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="venditoreImpresa"
                    checked={!form.venditoreImpresa}
                    onChange={() => handleChange('venditoreImpresa', false)}
                    className="form-radio text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Privato</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="venditoreImpresa"
                    checked={form.venditoreImpresa}
                    onChange={() => handleChange('venditoreImpresa', true)}
                    className="form-radio text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Impresa</span>
                </label>
              </div>
            </div>

            {/* Commissione Agenzia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commissione Agenzia
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={form.percentualeAgenzia}
                  onChange={(e) => handleChange('percentualeAgenzia', e.target.value)}
                  className="w-full pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="3"
                  min="0"
                  max="10"
                  step="0.1"
                />
                <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">%</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">Commissione standard 2-4%</p>
              {commissionePreview && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <div className="flex justify-between text-gray-700">
                    <span>Commissione netta</span>
                    <span>{formatCurrency(commissionePreview.netta.toString())}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>IVA 22%</span>
                    <span>{formatCurrency(commissionePreview.iva.toString())}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-amber-700 border-t border-amber-200 pt-1 mt-1">
                    <span>Totale (IVA incl.)</span>
                    <span>{formatCurrency(commissionePreview.totale.toString())}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sezione 3 — Catastale & Energia */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Catastale & Energia</h3>
          <div className="space-y-4">
            {/* Rendita catastale */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rendita Catastale
                {renderUnlockBadges('renditaCatastale')}
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">€</span>
                <input
                  type="number"
                  value={form.renditaCatastale}
                  onChange={(e) => handleChange('renditaCatastale', e.target.value)}
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="800"
                  min="0"
                  step="10"
                />
              </div>
            </div>

            {/* Classe energetica */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Classe Energetica
                {renderUnlockBadges('classeEnergetica')}
              </label>
              <select
                value={form.classeEnergetica}
                onChange={(e) => handleChange('classeEnergetica', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Seleziona...</option>
                {CLASSI_ENERGETICHE.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Sezione 4 — Note */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Note</h3>
          <textarea
            value={form.note}
            onChange={(e) => handleChange('note', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={5}
            placeholder="Note aggiuntive sull'immobile..."
            maxLength={2000}
          />
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saveImmobile.isPending}
          className="btn-primary px-8 py-3"
        >
          {saveImmobile.isPending ? 'Salvataggio...' : 'Salva Scheda Immobile'}
        </button>
      </div>
    </div>
  );
}
